/**
 * TextInput / TextArea: the shared free-text primitives.
 *
 * Thin wrappers over <input> / <textarea> that keep every native prop
 * (className stays the caller's FLD/FLD_TEXTAREA string; the Field
 * primitive stays the labeling contract) and pass what was typed through
 * unchanged. A value gets its capital where it is shown, through
 * displayValue (@/lib/valueCase), never in the box.
 *
 * A box that names a name kind (`nameCase`: person, place, role,
 * relationship or carrier) gives a value typed all in lower case its name
 * case when the person leaves the box: "kit o'neill-mcrae" reads
 * "Kit O'Neill-McRae". The cased value is delivered through the caller's
 * onChange, as a keystroke would be, before the caller's onBlur runs, so
 * controlled state and an uncontrolled box that saves on blur both read
 * it. Only a visit in which the person typed is cased: a box they merely
 * tabbed through keeps a stored "ruth pembrook" as it was. Never per
 * keystroke. A name-kind box also turns off the phone's own sentence
 * capital (autoCapitalize="none") unless the caller sets its own.
 *
 * A TextArea with `grow` follows its text: its height is set to the
 * text's height on mount and on every change, up to a cap of 20rem
 * (read from the root font size, so it follows the reader's text size),
 * after which the box scrolls. It never sets a width and starts at the
 * caller's `rows`.
 */

import {
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ComponentPropsWithRef,
  type ReactNode,
  type Ref,
} from 'react';
import { nameCase as toNameCase, type NameCaseKind } from '@/lib/valueCase';

/**
 * Set the cased value on the element, then deliver it through the
 * caller's onChange so the controlled-state contract is untouched:
 * handlers read target/currentTarget.value, which now carries the cased
 * text. No-op when name case changes nothing.
 */
function deliverNameCase<E extends HTMLInputElement | HTMLTextAreaElement>(
  el: E,
  kind: NameCaseKind,
  onChange: ((e: ChangeEvent<E>) => void) | undefined,
): void {
  const next = toNameCase(el.value, kind);
  if (next === el.value) return;
  el.value = next;
  onChange?.({ target: el, currentTarget: el } as unknown as ChangeEvent<E>);
}

/** Every native input prop passes through, `ref` among them (React 19
 *  treats ref as a regular prop), so a caller can move focus to the box. */
export interface TextInputProps extends ComponentPropsWithRef<'input'> {
  /** A name kind: the value takes its name case on leaving the box. */
  nameCase?: NameCaseKind;
}

export function TextInput({
  nameCase,
  autoCapitalize,
  onFocus,
  onBlur,
  onChange,
  ...rest
}: TextInputProps): ReactNode {
  const typed = useRef(false);
  return (
    <input
      {...rest}
      autoCapitalize={autoCapitalize ?? (nameCase !== undefined ? 'none' : undefined)}
      onFocus={(e) => {
        typed.current = false;
        onFocus?.(e);
      }}
      onChange={(e) => {
        typed.current = true;
        onChange?.(e);
      }}
      onBlur={(e) => {
        if (nameCase !== undefined && typed.current) deliverNameCase(e.currentTarget, nameCase, onChange);
        onBlur?.(e);
      }}
    />
  );
}

export interface TextAreaProps extends ComponentPropsWithRef<'textarea'> {
  /** A name kind: the value takes its name case on leaving the box. */
  nameCase?: NameCaseKind;
  /** The box's height follows its text to a cap of 20rem, then scrolls. */
  grow?: boolean;
}

/** The cap a growing box stops at, in px: 20rem at the root font size. */
function growCap(): number {
  return 20 * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

/** Sets a growing box's height to its text's, capped, and lets it
 *  scroll only past the cap. */
function fitToText(el: HTMLTextAreaElement): void {
  const cap = growCap();
  el.style.height = 'auto';
  const text = el.scrollHeight;
  el.style.height = `${Math.min(text, cap)}px`;
  el.style.overflowY = text > cap ? 'auto' : 'hidden';
}

function setRef<E>(ref: Ref<E> | undefined, el: E | null): void {
  if (typeof ref === 'function') ref(el);
  else if (ref) ref.current = el;
}

export function TextArea({
  nameCase,
  autoCapitalize,
  onFocus,
  onBlur,
  onChange,
  grow = false,
  className,
  ref,
  ...rest
}: TextAreaProps): ReactNode {
  const typed = useRef(false);
  const box = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (grow && box.current !== null) fitToText(box.current);
  }, [grow, rest.value]);
  return (
    <textarea
      {...rest}
      ref={(el) => {
        box.current = el;
        setRef(ref, el);
      }}
      className={grow ? [className, 'resize-none max-h-[20rem]'].filter(Boolean).join(' ') : className}
      autoCapitalize={autoCapitalize ?? (nameCase !== undefined ? 'none' : undefined)}
      onFocus={(e) => {
        typed.current = false;
        onFocus?.(e);
      }}
      onChange={(e) => {
        typed.current = true;
        if (grow) fitToText(e.currentTarget);
        onChange?.(e);
      }}
      onBlur={(e) => {
        if (nameCase !== undefined && typed.current) deliverNameCase(e.currentTarget, nameCase, onChange);
        onBlur?.(e);
      }}
    />
  );
}
